import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../services/api";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    user_id: "",
    password: "",
    role: "Technician",
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reset password popup
  const [showReset, setShowReset] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [generatedPass, setGeneratedPass] = useState("");

  // -------------------------
  // Load Users
  // -------------------------
  const fetchUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (err) {
      console.error("User fetch error:", err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // -------------------------
  // Add or Update User
  // -------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingId) {
        // 🔧 Update user (no password update in edit mode)
        await api.put(`/users/${editingId}`, {
          user_id: form.user_id,
          role: form.role,
        });
      } else {
        // ➕ Add new user
        await api.post("/users", form);
      }

      setForm({ user_id: "", password: "", role: "Technician" });
      setEditingId(null);
      fetchUsers();
    } catch (err) {
      console.error("User save error:", err);
      alert("Error saving user");
    }

    setLoading(false);
  };

  // -------------------------
  // Edit User
  // -------------------------
  const handleEdit = (user) => {
    setForm({
      user_id: user.user_id,
      password: "",
      role: user.role,
    });
    setEditingId(user.id);
  };

  // -------------------------
  // Delete User
  // -------------------------
  const handleDelete = async (id) => {
    if (!confirm("Delete this user?")) return;

    try {
      await api.delete(`/users/${id}`);
      fetchUsers();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Error deleting user");
    }
  };

  // -------------------------
  // Reset Password
  // -------------------------
  const handlePasswordReset = async () => {
    if (!selectedUser) return alert("Choose a user");

    try {
      const newPass = Math.random().toString(36).slice(-8);

      await api.post("/users/reset", {
        user_id: selectedUser,
        newPassword: newPass,
      });

      setGeneratedPass(newPass);
    } catch (err) {
      console.error("Reset error:", err);
      alert("Could not reset password");
    }
  };

  return (
    <Layout>
      <h1 className="text-3xl font-bold mb-6 text-indigo-600">Users</h1>

      {/* ADD / EDIT FORM */}
      <div className="bg-white p-5 rounded shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">
          {editingId ? "Edit User" : "Add User"}
        </h2>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-4 gap-4"
        >
          {/* USER ID */}
          <input
            type="text"
            placeholder="User ID"
            value={form.user_id}
            onChange={(e) => setForm({ ...form, user_id: e.target.value })}
            required
            className="p-2 border rounded w-full bg-white text-black border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />

          {/* PASSWORD (only in Add mode) */}
          {!editingId && (
            <input
              type="text"
              placeholder="Password"
              value={form.password}
              onChange={(e) =>
                setForm({ ...form, password: e.target.value })
              }
              required
              className="p-2 border rounded w-full bg-white text-black border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          )}

          {/* ROLE */}
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="p-2 border rounded w-full bg-white text-black border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="Admin">Admin</option>
            <option value="Technician">Technician</option>
          </select>

          {/* SUBMIT BUTTON */}
          <button
            type="submit"
            disabled={loading}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          >
            {loading ? "Saving..." : editingId ? "Update" : "Add"}
          </button>
        </form>
      </div>

      {/* USER LIST */}
      <div className="bg-white p-5 rounded shadow overflow-x-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">User List</h2>

          <button
            onClick={() => setShowReset(true)}
            className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
          >
            Reset Password
          </button>
        </div>

        <table className="w-full table-auto">
          <thead>
            <tr className="bg-gray-200">
              <th className="px-4 py-2">User ID</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>

          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b">
                <td className="px-4 py-2">{u.user_id}</td>
                <td className="px-4 py-2">{u.role}</td>
                <td className="px-4 py-2 space-x-3">
                  <button
                    onClick={() => handleEdit(u)}
                    className="text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(u.id)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* RESET PASSWORD POPUP */}
      {showReset && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-lg font-semibold mb-4">Reset Password</h3>

            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="p-2 border rounded w-full mb-4 bg-white text-black border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <option value="">Select User</option>
              {users.map((u) => (
                <option key={u.id} value={u.user_id}>
                  {u.user_id}
                </option>
              ))}
            </select>

            <button
              onClick={handlePasswordReset}
              className="bg-indigo-600 text-white px-4 py-2 rounded w-full hover:bg-indigo-700"
            >
              Generate Password
            </button>

            {generatedPass && (
              <div className="mt-4 bg-gray-100 p-2 rounded text-center text-black">
                <strong>New Password:</strong> {generatedPass}
              </div>
            )}

            <button
              onClick={() => {
                setShowReset(false);
                setGeneratedPass("");
              }}
              className="mt-4 text-red-600 hover:underline w-full"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
